import { z } from 'zod';
import type { PromotionDisplaySection } from '@sshop/domain';

import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';
import { productSummaryDto } from '../product/product.dto';
import { toProductSummary } from '../product/product.mapper';

import type { PromotionService } from './PromotionService';

const activePromotionsDto = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      description: z.string(),
      type: z.enum(['quantity', 'product', 'cart']),
      discount_type: z.enum(['percent', 'fixed']),
      discount_value: z.number().positive(),
      required_quantity: z.number().int().positive(),
      ends_at: z.string().nullable(),
      show_page: z.boolean().default(false),
      show_home: z.boolean().default(false),
      show_category: z.boolean().default(false),
      category_slugs: z.array(z.string()).default([]),
      brand_ids: z.array(z.string()).default([]),
      product_ids: z.array(z.string()).default([]),
    }),
  ),
});

const displaySectionDto = z.object({
  id: z.string(),
  layout: z.enum(['classic', 'featured', 'category_tabs', 'banner', 'mixed']),
  title: z.string(),
  subtitle: z.string(),
  description: z.string(),
  badge: z.string(),
  cta: z.string(),
  ctaUrl: z.string().nullable(),
  appearance: z
    .record(z.string(), z.string().nullable())
    .transform(
      (values) =>
        Object.fromEntries(Object.entries(values).filter(([, value]) => value !== null)) as Record<
          string,
          string
        >,
    ),
  backgroundImage: z.string().nullable(),
  bannerDesktop: z.string().nullable(),
  bannerMobile: z.string().nullable(),
  showCountdown: z.boolean(),
  showBadge: z.boolean(),
  endsAt: z.string().nullable(),
  showDesktop: z.boolean(),
  showMobile: z.boolean(),
  promotionIds: z.array(z.string()),
  featuredProductId: z.string(),
  defaultCategoryId: z.string(),
  products: z.array(productSummaryDto),
  tabs: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      banner: z.string().nullable(),
      seeAllUrl: z.string().nullable(),
      products: z.array(productSummaryDto),
    }),
  ),
  blocks: z.array(
    z.object({
      type: z.enum(['flash', 'bundle', 'best', 'collection']),
      title: z.string(),
      subtitle: z.string(),
      cta: z.string(),
      ctaUrl: z.string().nullable(),
      image: z.string().nullable(),
      accent: z.string(),
      size: z.enum(['regular', 'wide']),
      promotionId: z.string(),
      products: z.array(productSummaryDto),
    }),
  ),
});

function toDisplaySection(section: z.infer<typeof displaySectionDto>): PromotionDisplaySection {
  return {
    ...section,
    products: section.products.map(toProductSummary),
    tabs: section.tabs.map((tab) => ({ ...tab, products: tab.products.map(toProductSummary) })),
    blocks: section.blocks.map((block) => ({
      ...block,
      products: block.products.map(toProductSummary),
    })),
  };
}

export function createLaravelPromotionService(transport: Transport): PromotionService {
  return {
    async active(signal) {
      const response = await transport.request({
        method: 'GET',
        path: '/promotions/active',
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(activePromotionsDto, response.data, 'GET /promotions/active');
      return parsed.data.map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        type: item.type,
        discountType: item.discount_type,
        discountValue: item.discount_value,
        requiredQuantity: item.required_quantity,
        endsAt: item.ends_at,
        showPage: item.show_page,
        showHome: item.show_home,
        showCategory: item.show_category,
        categorySlugs: item.category_slugs,
        brandIds: item.brand_ids,
        targetProductIds: item.product_ids,
      }));
    },
    async displaySections(signal) {
      const response = await transport.request({
        method: 'GET',
        path: '/promotions/display-sections',
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(
        z.object({ data: z.array(displaySectionDto) }),
        response.data,
        'GET /promotions/display-sections',
      );
      return parsed.data.map(toDisplaySection);
    },
    async previewSection(id, expires, signature, signal) {
      const response = await transport.request({
        method: 'GET',
        path: `/promotions/display-sections/${encodeURIComponent(id)}/preview`,
        query: { expires, signature },
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(
        z.object({ data: displaySectionDto }),
        response.data,
        'GET /promotions/display-sections/{id}/preview',
      );
      return toDisplaySection(parsed.data);
    },
  };
}
